// The restock suggestion (M6, plan §1 "basic"): what to order, worked out from a branch's
// real stock and sales or from a sales replay (`replay_scenario_id`), with every number
// explained — the sales rate over in-stock days, the lead time, the shelf-life ceiling,
// the pack rounding, min / par. Chosen lines become purchase requests (one per supplier).
// Nothing reads as "nothing to order" unless it is covered: a product that is short but
// can't be ordered, or one without usable sales data, says so (UI6A-3). A replay's numbers
// are the replay's stock, not yours, so a replay never makes purchase requests (UI6A-4).
// What the branch's requests still waiting for a decision ask for counts like stock on order,
// and each is named; such a product starts unticked, so nobody asks for it twice (UI6A-101).

import type { RestockItem } from '@enp/domain/api';
import { isPositive } from '@enp/domain/quantity';
import {
  Badge,
  Button,
  Checkbox,
  cn,
  DateField,
  EmptyState,
  IconButton,
  Input,
  Money,
  PageHeader,
  Skeleton,
} from '@enp/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ChevronDown,
  ChevronRight,
  History,
  PackageSearch,
  Send,
  SlidersHorizontal,
  TriangleAlert,
  X,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { meQueryOptions } from '../../../../auth/session.js';
import {
  canAskFrom,
  chosenByDefault,
  raisedToMinimum,
  rateKnown,
  requestedQty,
  type SuggestionKind,
  suggestionOutcome,
  suggestionTally,
} from '../../../../purchasing/figures.js';
import { LevelDialog } from '../../../../purchasing/levels.js';
import {
  BaseQty,
  KeyHints,
  NoPermission,
  PurchasingViews,
  useBranches,
  useCan,
  usePageKeys,
} from '../../../../purchasing/parts.js';
import {
  allSuppliersQuery,
  type RestockSearch,
  restockQuery,
  restockSearchSchema,
} from '../../../../purchasing/queries.js';
import { RestockExplanation, shownRestockFlags } from '../../../../purchasing/restock-parts.js';
import { RequestFromSuggestionDialog } from '../../../../purchasing/restock-request.js';
import { daysText, RESTOCK_FLAGS } from '../../../../purchasing/words.js';
import { scenariosQuery } from '../../../../replay/queries.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { Notice } from '../../../../stations/page-parts.js';
import { FilterSelect } from '../../../../stock/components.js';
import { plainDateLabel, accountToday } from '../../../../stock/labels.js';
import { clean } from '../../../../stock/queries.js';

export const Route = createFileRoute('/_app/purchasing/restock/')({
  validateSearch: restockSearchSchema,
  component: RestockPage,
});

const orderable = (i: RestockItem) => isPositive(i.suggested_base_qty) && i.supplier_item !== null;

/** The table's cells: tighter below xl, so the columns fit 720 px at 1024 wide. */
const CELL = 'px-2 py-2 xl:px-3';
/** Header cells stick under the top bar while the page scrolls. */
const HEAD = 'sticky top-14 z-10 bg-surface-sunken px-2 py-2 xl:px-3';
/** Pinned while a narrow table scrolls sideways (below md). */
const PIN_CHECK = 'max-md:sticky max-md:left-0 max-md:z-[11]';
const PIN_PRODUCT = 'max-md:sticky max-md:left-10 max-md:z-[11]';

const OUTCOME_CLASS: Record<SuggestionKind, string> = {
  order: 'font-semibold text-fg',
  short: 'font-semibold text-state-warning',
  no_data: 'font-semibold text-state-warning',
  covered: 'text-muted',
};

/** A number field of the settings bar: typed freely, applied on blur or Enter. */
function DaysField({
  id,
  label,
  value,
  fallback,
  min,
  max,
  onApply,
}: {
  id: string;
  label: string;
  value: number | undefined;
  fallback: number;
  min: number;
  max: number;
  onApply: (v: number | undefined) => void;
}) {
  const [text, setText] = useState(String(value ?? fallback));
  const [bad, setBad] = useState(false);
  const apply = () => {
    const n = Number(text);
    if (!Number.isInteger(n) || n < min || n > max) {
      setBad(true);
      return;
    }
    setBad(false);
    onApply(n === fallback ? undefined : n);
  };
  return (
    <span className="flex items-center gap-2">
      <label htmlFor={id} className="text-sm text-muted">
        {label}
      </label>
      <Input
        id={id}
        inputMode="numeric"
        value={text}
        aria-invalid={bad || undefined}
        aria-describedby={bad ? `${id}-error` : undefined}
        onChange={(e) => setText(e.target.value)}
        onBlur={apply}
        onKeyDown={(e) => e.key === 'Enter' && apply()}
        className="w-20 text-right tabular-nums"
      />
      {bad && (
        <span id={`${id}-error`} role="alert" className="text-xs font-medium text-state-danger">
          {min} to {max}
        </span>
      )}
    </span>
  );
}

function RestockPage() {
  useDocumentTitle('Restock suggestion');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const canRequest = useCan('purchasing.request');
  const canLevels = useCan('purchasing.order');
  const allBranches = me.branch_scope === null;
  const { branches } = useBranches();
  const scenarios = useQuery({ ...scenariosQuery(), enabled: allBranches });
  const suppliers = useQuery(allSuppliersQuery());
  const q = useQuery(restockQuery(search));
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [levelFor, setLevelFor] = useState<RestockItem | null>(null);

  const setSearch = (patch: Partial<RestockSearch>) => {
    setChosen(null);
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });
  };

  const data = q.data;
  const everything = useMemo(() => data?.items ?? [], [data]);
  const tally = useMemo(() => suggestionTally(everything), [everything]);
  // Covered products only when asked for; to order, short and without data always.
  const items = useMemo(
    () =>
      search.all ? everything : everything.filter((i) => suggestionOutcome(i).kind !== 'covered'),
    [everything, search.all],
  );
  const replay = data !== undefined && !canAskFrom(data.source);
  // Everything with something to order is chosen until the person changes it.
  // …except a product a request still waiting for a decision already asks for (UI6A-101).
  const selected = chosen ?? new Set(items.filter(chosenByDefault).map((i) => i.product.id));
  const selectedItems = items.filter((i) => selected.has(i.product.id) && orderable(i));
  const selectedValue = selectedItems.reduce((s, i) => s + (i.est_value_cents ?? 0), 0);
  const selectedUnpriced = selectedItems.filter((i) => i.est_value_cents === null).length;
  const scenario = scenarios.data?.items.find((s) => s.id === search.scenario);
  const history = data
    ? data.source === 'replay'
      ? `the sales replay “${scenario?.name ?? data.branch.name}”`
      : `${daysText(data.window.days)} of sales at ${data.branch.name}`
    : '';

  const sourceValue = search.scenario
    ? `replay:${search.scenario}`
    : search.branch
      ? `branch:${search.branch}`
      : undefined;
  const sourceOptions = [
    ...branches.map((b) => ({
      value: `branch:${b.id}`,
      label: `${b.name}: real stock and sales`,
      group: 'Your branches',
    })),
    ...(scenarios.data?.items ?? [])
      .filter((s) => s.archived_at === null)
      .map((s) => ({
        value: `replay:${s.id}`,
        label: `Replay: ${s.name}`,
        group: 'Sales replays',
      })),
  ];

  const canAsk = !replay && canRequest === true && selectedItems.length > 0;
  usePageKeys({ a: canAsk ? () => setRequesting(true) : undefined });

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChosen(next);
  };
  const toggleOpen = (id: string) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpen(next);
  };

  return (
    <>
      <PageHeader
        title="Purchasing"
        description="What to order next, worked out from past sales, stock on hand and on order, lead times and shelf life. Every number says how it was worked out. It suggests; you decide."
        actions={
          <Button variant="secondary" asChild>
            <Link to="/purchasing/restock/levels">
              <SlidersHorizontal aria-hidden />
              Minimum and par levels
            </Link>
          </Button>
        }
      />
      <PurchasingViews current="restock" />

      <section aria-label="What the suggestion is based on" className="mb-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            label="History"
            value={sourceValue}
            allLabel="Default branch: real stock and sales"
            options={sourceOptions}
            onChange={(v) =>
              setSearch(
                v?.startsWith('replay:')
                  ? { scenario: v.slice(7), branch: undefined }
                  : { branch: v?.slice(7), scenario: undefined },
              )
            }
            className="max-w-80"
          />
          <FilterSelect
            label="Supplier"
            value={search.supplier}
            allLabel="All suppliers"
            options={(suppliers.data?.items ?? []).map((s) => ({ value: s.id, label: s.name }))}
            onChange={(v) => setSearch({ supplier: v })}
          />
          <span className="flex items-center gap-2">
            <label htmlFor="restock-as-of" className="text-sm text-muted">
              Up to
            </label>
            <DateField
              id="restock-as-of"
              value={search.as_of}
              today={today}
              placeholder="Today"
              onChange={(v) => setSearch({ as_of: v })}
              wrapperClassName="w-40"
            />
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <DaysField
            key={`days-${search.days ?? ''}`}
            id="restock-days"
            label="Days of history"
            value={search.days}
            fallback={84}
            min={7}
            max={365}
            onApply={(v) => setSearch({ days: v })}
          />
          <DaysField
            key={`review-${search.review ?? ''}`}
            id="restock-review"
            label="Days to the next order"
            value={search.review}
            fallback={7}
            min={0}
            max={90}
            onApply={(v) => setSearch({ review: v })}
          />
          <DaysField
            key={`safety-${search.safety ?? ''}`}
            id="restock-safety"
            label="Safety days"
            value={search.safety}
            fallback={0}
            min={0}
            max={90}
            onApply={(v) => setSearch({ safety: v })}
          />
          <label className="flex items-center gap-2 text-sm text-fg">
            <Checkbox
              checked={Boolean(search.all)}
              onCheckedChange={(v) => setSearch({ all: v === true ? true : undefined })}
            />
            Show covered products too
          </label>
        </div>
      </section>

      {q.isError && !data ? (
        <ErrorPanel
          error={q.error}
          titleAs="h2"
          what="The restock suggestion"
          onRetry={() => void q.refetch()}
        />
      ) : !data ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <span role="status" className="sr-only">
            Working out the suggestion…
          </span>
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-body text-fg" data-testid="restock-coverage" aria-live="polite">
            <span className="font-medium">
              {data.source === 'replay' ? 'From the replay' : `At ${data.branch.name}`}, sales{' '}
              {plainDateLabel(data.window.from, today)} to {plainDateLabel(data.window.to, today)} (
              {daysText(data.window.days)}).
            </span>{' '}
            {data.coverage.summary}
            {q.isFetching && <span className="text-muted"> · Updating…</span>}
          </p>
          {everything.length > 0 && (
            <ul
              aria-label="What the suggestion found"
              className="flex flex-wrap gap-2 text-sm"
              data-testid="restock-tally"
            >
              <li>
                <Badge tone={tally.order > 0 ? 'primary' : 'neutral'}>{tally.order} to order</Badge>
              </li>
              {tally.short > 0 && (
                <li>
                  <Badge tone="warning">
                    <TriangleAlert aria-hidden />
                    {tally.short} short, nothing orderable
                  </Badge>
                </li>
              )}
              {tally.no_data > 0 && (
                <li>
                  <Badge tone="warning">
                    <TriangleAlert aria-hidden />
                    {tally.no_data} without usable sales data
                  </Badge>
                </li>
              )}
              <li>
                <Badge tone="neutral">
                  {tally.covered} covered{!search.all && tally.covered > 0 ? ' (hidden)' : ''}
                </Badge>
              </li>
            </ul>
          )}
          {replay && (
            <Notice tone="warning" title="Worked out from a sales replay: you can’t ask from it">
              Sales, stock on hand and on order are the replay’s ({history}), not your own stock, so
              its quantities are wrong for any real branch. Use it to check the suggestion against
              past sales. To ask for stock, switch the history to a branch’s real stock.
              <span className="mt-2 block">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setSearch({ scenario: undefined, branch: undefined })}
                >
                  Use real stock and sales
                </Button>
              </span>
            </Notice>
          )}
          {items.length === 0 ? (
            <EmptyState
              icon={PackageSearch}
              title={
                data.coverage.products_considered === 0
                  ? 'Nothing to work out yet'
                  : 'Nothing to order: every product is covered'
              }
              description={
                data.coverage.products_considered === 0
                  ? 'The suggestion covers products you buy from a supplier. Add suppliers and what you buy from them first.'
                  : 'Stock on hand and on order lasts every product until the next order, and none is short or without sales data.'
              }
              action={
                data.coverage.products_considered === 0 ? (
                  <Button asChild>
                    <Link to="/purchasing/suppliers">Open suppliers</Link>
                  </Button>
                ) : !search.all ? (
                  <Button variant="secondary" onClick={() => setSearch({ all: true })}>
                    Show the covered products
                  </Button>
                ) : undefined
              }
            />
          ) : (
            // The page scrolls, not a box inside it: the header row sticks under the top bar,
            // and the columns fit 976 px (1280 wide) and 720 px (1024) without sideways
            // scrolling (loop 3). Below xl the estimate folds under the suggestion; below md
            // the table may scroll sideways, with the checkbox and product pinned.
            <div className="rounded-lg border border-border bg-surface-raised max-md:overflow-x-auto">
              <table aria-label="Restock suggestion" className="w-full border-collapse text-body">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted">
                    <th scope="col" className={cn(HEAD, 'w-10 rounded-tl-lg', PIN_CHECK)}>
                      <span className="sr-only">Choose</span>
                    </th>
                    <th scope="col" className={cn(HEAD, PIN_PRODUCT)}>
                      Product
                    </th>
                    <th scope="col" className={cn(HEAD, 'whitespace-nowrap text-right')}>
                      On hand
                    </th>
                    <th scope="col" className={cn(HEAD, 'hidden text-right lg:table-cell')}>
                      On order / asked
                    </th>
                    <th scope="col" className={cn(HEAD, 'text-right')}>
                      Used a day
                    </th>
                    <th scope="col" className={cn(HEAD, 'text-right')}>
                      Suggested
                      <span className="xl:hidden">
                        <span aria-hidden> · </span>
                        <span className="sr-only">, </span>
                        estimate
                      </span>
                    </th>
                    <th scope="col" className={cn(HEAD, 'hidden text-right xl:table-cell')}>
                      Estimate
                    </th>
                    <th scope="col" className={cn(HEAD, 'w-12 rounded-tr-lg')}>
                      <span className="sr-only">Why</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => {
                    const id = i.product.id;
                    const unit = i.product.base_uom;
                    const expanded = open.has(id);
                    const can = orderable(i) && !replay;
                    const outcome = suggestionOutcome(i);
                    const raised = raisedToMinimum(i);
                    const flags = shownRestockFlags(i).filter(
                      (f) =>
                        f.code !== 'already_requested' || (i.waiting_requests ?? []).length === 0,
                    );
                    const estimate =
                      i.est_value_cents === null ? (
                        <span className="text-sm text-muted">
                          {outcome.kind === 'order' ? 'No price' : '—'}
                        </span>
                      ) : (
                        <Money cents={i.est_value_cents} className="text-fg" />
                      );
                    return (
                      <Fragment key={id}>
                        <tr
                          className={cn(
                            'border-t border-border align-top',
                            expanded && 'bg-surface-sunken/40',
                          )}
                          data-product={id}
                        >
                          <td className={cn(CELL, PIN_CHECK, 'max-md:bg-surface-raised')}>
                            {can ? (
                              <Checkbox
                                aria-label={`Order ${i.product.name}`}
                                checked={selected.has(id)}
                                onCheckedChange={() => toggle(id)}
                              />
                            ) : null}
                          </td>
                          <td className={cn(CELL, PIN_PRODUCT, 'max-md:bg-surface-raised')}>
                            <span className="flex flex-col gap-0.5 leading-tight">
                              <span className="font-medium text-fg">{i.product.name}</span>
                              <span className="text-xs text-muted">
                                {i.supplier ? i.supplier.name : 'No supplier'}
                                {i.supplier_item && ` · ${i.supplier_item.pack_uom} of `}
                                {i.supplier_item && (
                                  <BaseQty value={i.supplier_item.base_qty_per_pack} unit={unit} />
                                )}
                              </span>
                              {(flags.length > 0 || (i.waiting_requests ?? []).length > 0) && (
                                <span className="flex flex-wrap gap-1">
                                  {/* A waiting request, named and one click away (UI6A-101). */}
                                  {(i.waiting_requests ?? []).map((r) => (
                                    <Badge key={r.id} tone="warning" data-testid="already-asked">
                                      <TriangleAlert aria-hidden />
                                      Asked on{' '}
                                      <Link
                                        to="/purchasing/requests/$requestId"
                                        params={{ requestId: r.id }}
                                        className="whitespace-nowrap font-mono underline underline-offset-2"
                                      >
                                        {r.number}
                                      </Link>{' '}
                                      (waiting)
                                    </Badge>
                                  ))}
                                  {flags.map((f) => (
                                    <Badge key={f.code} tone="warning" data-flag={f.code}>
                                      <TriangleAlert aria-hidden />
                                      {RESTOCK_FLAGS[f.code]?.title ?? f.code}
                                    </Badge>
                                  ))}
                                </span>
                              )}
                            </span>
                          </td>
                          <td className={cn(CELL, 'text-right')}>
                            <BaseQty value={i.explanation.inputs.on_hand_qty} unit={unit} />
                          </td>
                          <td className={cn(CELL, 'hidden text-right lg:table-cell')}>
                            <span className="flex flex-col items-end leading-tight">
                              <BaseQty value={i.explanation.inputs.on_order_qty} unit={unit} />
                              {isPositive(requestedQty(i)) && (
                                <span
                                  className="whitespace-nowrap text-xs font-medium text-state-warning"
                                  data-testid="asked-qty"
                                >
                                  + <BaseQty value={requestedQty(i)} unit={unit} /> asked
                                </span>
                              )}
                            </span>
                          </td>
                          <td className={cn(CELL, 'text-right')}>
                            <span
                              className={cn(
                                'whitespace-nowrap tabular-nums',
                                !rateKnown(i) && 'text-muted',
                              )}
                              data-testid="rate"
                            >
                              {rateKnown(i) ? (
                                <BaseQty value={i.explanation.inputs.daily_rate} unit={unit} />
                              ) : (
                                'Unknown'
                              )}
                            </span>
                          </td>
                          <td className={cn(CELL, 'text-right')}>
                            <span
                              className="ml-auto flex max-w-48 flex-col items-end gap-1"
                              data-testid="suggested"
                              data-outcome={outcome.kind}
                            >
                              <span className={OUTCOME_CLASS[outcome.kind]}>{outcome.text}</span>
                              {raised && (
                                <span
                                  className="text-xs text-state-warning"
                                  data-testid="raised-to-minimum"
                                >
                                  {raised.text}
                                </span>
                              )}
                              {outcome.kind === 'order' && (
                                <span className="text-sm xl:hidden" data-testid="estimate-folded">
                                  {estimate}
                                </span>
                              )}
                              {outcome.kind === 'no_data' && canLevels && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  icon={<SlidersHorizontal aria-hidden />}
                                  onClick={() => setLevelFor(i)}
                                >
                                  Set minimum / par…
                                  <span className="sr-only"> for {i.product.name}</span>
                                </Button>
                              )}
                            </span>
                          </td>
                          <td className={cn(CELL, 'hidden text-right xl:table-cell')}>
                            {estimate}
                          </td>
                          <td className={cn(CELL, 'text-right')}>
                            <IconButton
                              noTooltip
                              label={`Why ${i.product.name}: how it was worked out`}
                              aria-expanded={expanded}
                              aria-controls={`why-${id}`}
                              icon={
                                expanded ? (
                                  <ChevronDown aria-hidden />
                                ) : (
                                  <ChevronRight aria-hidden />
                                )
                              }
                              onClick={() => toggleOpen(id)}
                            />
                          </td>
                        </tr>
                        {expanded && (
                          // The whole width, under the row it explains.
                          <tr id={`why-${id}`} className="bg-surface-sunken/40">
                            <td colSpan={8} className="px-4 pb-4 pt-1">
                              <div className="max-md:sticky max-md:left-0 max-md:w-[calc(100vw-2rem)]">
                                <RestockExplanation item={i} />
                                {raised?.addTo && (
                                  <p className="mt-2 text-sm text-fg" data-testid="add-to-request">
                                    Less is needed than the minimum, and{' '}
                                    <Link
                                      to="/purchasing/requests/$requestId"
                                      params={{ requestId: raised.addTo.id }}
                                      className="whitespace-nowrap rounded-sm font-mono text-primary-text underline underline-offset-2"
                                    >
                                      {raised.addTo.number}
                                    </Link>{' '}
                                    already asks for it: add to that request (the approver can
                                    change its packs) rather than ask for the minimum again.
                                  </p>
                                )}
                                {canLevels && (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="mt-3"
                                    icon={<SlidersHorizontal aria-hidden />}
                                    onClick={() => setLevelFor(i)}
                                  >
                                    Set minimum / par…
                                    <span className="sr-only"> for {i.product.name}</span>
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {items.some(orderable) && replay && (
            <div className="sticky bottom-0 z-20 -mx-1 flex flex-wrap items-center justify-end gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3 shadow-xs">
              <p className="mr-auto text-body text-fg" data-testid="replay-no-ask">
                From a replay: switch to real stock to ask for these.
              </p>
              <Button
                variant="secondary"
                onClick={() => setSearch({ scenario: undefined, branch: undefined })}
              >
                Use real stock and sales
              </Button>
            </div>
          )}
          {items.some(orderable) && !replay && (
            <div className="sticky bottom-0 z-20 -mx-1 flex flex-wrap items-center justify-end gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3 shadow-xs">
              <KeyHints keys={canRequest ? [['a', 'ask for these']] : []} />
              <p className="mr-auto text-body text-fg" aria-live="polite">
                {selectedItems.length === 0 ? (
                  'Nothing chosen.'
                ) : (
                  <>
                    {selectedItems.length} {selectedItems.length === 1 ? 'product' : 'products'}{' '}
                    chosen · <Money cents={selectedValue} className="font-semibold" /> ex VAT
                    {selectedUnpriced > 0 && (
                      <span className="text-state-warning">
                        {' '}
                        (without {selectedUnpriced} unpriced)
                      </span>
                    )}
                  </>
                )}
              </p>
              {chosen !== null && (
                <Button variant="ghost" icon={<X aria-hidden />} onClick={() => setChosen(null)}>
                  Reset choice
                </Button>
              )}
              {canRequest === false ? (
                <NoPermission>
                  Asking for stock needs the “request purchases” permission.
                </NoPermission>
              ) : (
                <Button
                  icon={<Send aria-hidden />}
                  disabled={selectedItems.length === 0}
                  aria-keyshortcuts="a"
                  onClick={() => setRequesting(true)}
                >
                  Ask for these…
                </Button>
              )}
            </div>
          )}
          <p className="flex items-center gap-2 text-sm text-muted">
            <History aria-hidden className="size-4" />
            Sales in every expiry state count; days a product was out of stock all day are left out
            of its rate.
          </p>
        </div>
      )}
      {requesting && data && canAskFrom(data.source) && (
        <RequestFromSuggestionDialog
          data={data}
          items={selectedItems}
          history={history}
          today={today}
          onClose={() => setRequesting(false)}
        />
      )}
      {levelFor && (
        <LevelDialog product={levelFor.product} level={null} onClose={() => setLevelFor(null)} />
      )}
    </>
  );
}
